import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search, Filter, Inbox, Clock, CheckCircle2,
  Lightbulb, AlertTriangle, MessageSquare, Info, Send, Sparkles,
} from "lucide-react";

const TYPES = [
  { value: "probleme",   label: "Problème",      icon: AlertTriangle, color: "bg-pink-100 text-pink-600 border-pink-200" },
  { value: "idee",       label: "Idée",          icon: Lightbulb,     color: "bg-sun-100 text-sun-700 border-sun-200" },
  { value: "suggestion", label: "Suggestion",    icon: MessageSquare, color: "bg-mint-100 text-mint-600 border-mint-200" },
  { value: "info",       label: "Demande d'info", icon: Info,         color: "bg-plum-100 text-plum-700 border-plum-200" },
];

function typeMeta(v) { return TYPES.find((t) => t.value === v) || TYPES[1]; }

const STATUS_FILTERS = [
  { id: "all",      label: "Toutes" },
  { id: "open",     label: "En attente" },
  { id: "answered", label: "Répondue" },
];

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function isWithinDays(iso, days) {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) < days * 24 * 3600 * 1000;
}

export default function FeedbackPage() {
  const [type, setType] = useState("idee");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [query, setQuery] = useState("");

  const loadMine = async () => {
    try {
      const { data } = await api.get("/feedback/mine");
      setMine(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    loadMine();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return toast.error("Titre et message requis.");
    setBusy(true);
    try {
      await api.post("/feedback", { type, title, content });
      window.dispatchEvent(new CustomEvent("istres-action-completed", { detail: { kind: "feedback" } }));
      toast.success("Remontée envoyée à la mairie ✓");
      setTitle(""); setContent(""); setType("idee");
      loadMine();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mine.filter((f) => {
      if (statusFilter !== "all" && f.status !== statusFilter) return false;
      if (typeFilter !== "all" && f.type !== typeFilter) return false;
      if (q) {
        const hay = `${f.title} ${f.content} ${f.admin_reply || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [mine, statusFilter, typeFilter, query]);

  const counts = useMemo(() => ({
    all: mine.length,
    open: mine.filter((f) => f.status === "open").length,
    answered: mine.filter((f) => f.status === "answered").length,
  }), [mine]);

  return (
    <div className="space-y-10" data-testid="feedback-page">
      <header>
        <div className="phenix-eyebrow mb-3">Mes remontées</div>
        <h1 className="heading-display text-3xl lg:text-4xl text-plum-900">
          Vos demandes et leurs <span className="text-pink-500">réponses</span>.
        </h1>
        <p className="mt-3 text-[15px] text-plum-700/75 max-w-2xl">
          Suivez l&apos;état d&apos;avancement de chaque remontée envoyée à la mairie. Recevez une notification dès qu&apos;une réponse arrive.
        </p>
      </header>

      {/* KPI cards */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="feedback-kpis">
        <KpiCard label="Envoyées" value={counts.all} icon={Inbox} color="bg-plum-50 text-plum-700" />
        <KpiCard label="En attente" value={counts.open} icon={Clock} color="bg-sun-50 text-sun-700" />
        <KpiCard label="Répondues" value={counts.answered} icon={CheckCircle2} color="bg-mint-50 text-mint-600" />
      </div>

      <section className="grid lg:grid-cols-12 gap-8">
        {/* Form */}
        <aside className="lg:col-span-5 phenix-card bg-white p-6 h-fit" data-testid="feedback-form-card">
          <div className="phenix-eyebrow mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} strokeWidth={2.5} /> Nouvelle remontée
          </div>
          <h2 className="heading-display text-xl text-plum-900 mb-1">Envoyez-nous votre message</h2>
          <p className="text-[13px] text-plum-700/65 mb-5">La mairie répond généralement sous 5 jours ouvrés.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[12px] font-bold text-plum-700/75 uppercase tracking-wider">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="feedback-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="inline-flex items-center gap-2"><t.icon size={13} /> {t.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px] font-bold text-plum-700/75 uppercase tracking-wider">Titre</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex. Lampadaire en panne rue Truc"
                className="mt-1.5 rounded-xl"
                maxLength={120}
                data-testid="feedback-title"
              />
            </div>
            <div>
              <Label className="text-[12px] font-bold text-plum-700/75 uppercase tracking-wider">Message</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Décrivez votre demande en quelques phrases…"
                rows={5}
                maxLength={1500}
                className="mt-1.5 rounded-xl"
                data-testid="feedback-content"
              />
              <div className="text-[11px] text-plum-700/45 mt-1 text-right">{content.length}/1500</div>
            </div>
            <Button
              type="submit"
              disabled={busy || !title.trim() || !content.trim()}
              data-testid="feedback-submit"
              className="rounded-full bg-plum-700 hover:bg-plum-800 disabled:opacity-60 text-white w-full h-12 font-bold inline-flex items-center justify-center gap-2"
            >
              {busy ? "Envoi…" : <>Envoyer à la mairie <Send size={14} /></>}
            </Button>
          </form>
        </aside>

        {/* History */}
        <div className="lg:col-span-7">
          {/* Filter bar */}
          <div className="phenix-card bg-white p-3 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-1 relative min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-plum-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher dans mes remontées…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-full bg-cream-50 border border-cream-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-plum-200"
                  data-testid="feedback-search"
                />
              </div>
              <div className="inline-flex items-center gap-1 bg-cream-50 rounded-full p-1 border border-cream-200" role="tablist" data-testid="feedback-status-tabs">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStatusFilter(s.id)}
                    data-testid={`feedback-status-${s.id}`}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition-colors ${statusFilter === s.id ? "bg-plum-700 text-white" : "text-plum-700/70 hover:text-plum-700"}`}
                  >
                    {s.label} <span className="opacity-60 ml-0.5">({counts[s.id]})</span>
                  </button>
                ))}
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="rounded-full bg-cream-50 border-cream-200 h-9 text-[12px] w-auto px-3 gap-1.5" data-testid="feedback-type-filter">
                  <Filter size={12} /><SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* List */}
          {filtered.length === 0 ? (
            <div className="phenix-card bg-white p-10 text-center">
              <Inbox size={28} className="mx-auto text-plum-700/30 mb-2" />
              <h3 className="font-display font-bold text-plum-900">
                {mine.length === 0 ? "Aucune remontée envoyée" : "Aucun résultat"}
              </h3>
              <p className="text-[13px] text-plum-700/65 mt-1">
                {mine.length === 0 ? "Envoyez votre première remontée ci-contre." : "Essayez d'élargir vos filtres."}
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="my-feedback-list">
              {filtered.map((f) => <FeedbackCard key={f.id} f={f} />)}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="phenix-card bg-white p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-2xl ${color} grid place-items-center shrink-0`}>
        <Icon size={18} strokeWidth={2.2} />
      </div>
      <div>
        <div className="heading-display text-2xl text-plum-900 leading-none">{value}</div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-plum-700/55 mt-1">{label}</div>
      </div>
    </div>
  );
}

function FeedbackCard({ f }) {
  const t = typeMeta(f.type);
  const isAnswered = f.status === "answered";
  const isNewReply = isAnswered && f.answered_at && isWithinDays(f.answered_at, 14);
  return (
    <li
      className={`phenix-card bg-white p-5 ${isNewReply ? "ring-2 ring-mint-300/70" : ""}`}
      data-testid={`feedback-item-${f.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-2xl border ${t.color} grid place-items-center shrink-0`}>
          <t.icon size={18} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`phenix-pill ${t.color} text-[10px]`}>{t.label}</span>
            {isAnswered ? (
              <span className="phenix-pill bg-mint-50 text-mint-600 border-mint-200 text-[10px] inline-flex items-center gap-1">
                <CheckCircle2 size={10} /> Répondue
              </span>
            ) : (
              <span className="phenix-pill bg-sun-50 text-sun-700 border-sun-200 text-[10px] inline-flex items-center gap-1">
                <Clock size={10} /> En attente
              </span>
            )}
            {isNewReply && <span className="phenix-pill bg-pink-500 text-white text-[10px]">Nouvelle réponse</span>}
            <span className="text-[11px] text-plum-700/50 ml-auto">{fmtDate(f.created_at)}</span>
          </div>
          <h3 className="heading-display text-[1.05rem] text-plum-900 leading-tight">{f.title}</h3>
          <p className="text-[13px] text-plum-700/85 mt-1 leading-relaxed whitespace-pre-line">{f.content}</p>
          {isAnswered && f.admin_reply && (
            <div className="mt-3 bg-mint-50/50 border border-mint-200 rounded-2xl p-3">
              <div className="phenix-eyebrow text-mint-600 mb-1 inline-flex items-center gap-1">
                <CheckCircle2 size={10} /> Réponse de la mairie · {fmtDate(f.answered_at)}
              </div>
              <p className="text-[13px] text-plum-900 leading-snug whitespace-pre-line">{f.admin_reply}</p>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
